import { useCallback } from 'react';
import StripeService from '../services/StripeService';
import { refreshSession } from '../services/RefreshSession';

interface CheckoutParams {
  title: string;
  priceId: string;
  quantity?: number;
}

interface CheckoutFlowState {
  redirectToCheckout: (params: CheckoutParams) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook to manage Stripe checkout flow.
 * Centralizes checkout session creation and error handling.
 *
 * Handles:
 * - Session retrieval for customer email
 * - Stripe checkout session creation
 * - Error handling and user feedback
 * - Price ID validation
 */
export function useCheckoutFlow(): CheckoutFlowState {
  const redirectToCheckout = useCallback(async (params: CheckoutParams): Promise<void> => {
    const { title, priceId, quantity = 1 } = params;

    if (!priceId) {
      console.warn(`No priceId defined for ${title} plan`);
      alert('This plan is not available for purchase at the moment. Please contact support.');
      return;
    }

    try {
      let customerEmail: string | undefined;
      try {
        const session = await refreshSession();
        customerEmail = (session?.tokens?.idToken?.payload?.email as string) || undefined;
      } catch (error) {
        console.warn('Could not get user email:', error);
      }

      await StripeService.redirectToCheckout({
        title,
        priceId,
        quantity,
        customerEmail,
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('There was an error processing your request. Please try again or contact support.');
      throw error;
    }
  }, []);

  return {
    redirectToCheckout,
    isLoading: false,
    error: null,
  };
}
